import React, { useState, useEffect } from "react";
import { Select } from "antd";
import { list as getUserList } from "../controllers/V1/customerDue/dueController";

/**
 * Reusable User Select Component with Search and Select All functionality
 * 
 * @param {Array|String} value - Selected user ID(s) - Array for multiple, String for single
 * @param {Function} onChange - Callback function when selection changes
 * @param {String} placeholder - Placeholder text (default: "Select Users")
 * @param {String} className - CSS class name
 * @param {Object} style - Inline styles
 * @param {Number} maxTagCount - Maximum number of tags to show (default: 3)
 * @param {Boolean} showSearch - Enable search functionality (default: true)
 * @param {String} mode - Select mode: "multiple", "tags", etc. (default: "tags")
 * @param {Array} userTypes - Filter users by types (default: ["BDM", "Manager", "Sales Supervisor", "Sales Person"])
 * @param {Object} condition - Additional conditions for user filtering
 * @param {Boolean} multiple - Enable multiple selection (default: true). Set to false for single selection
 * @param {Boolean} disableOptions - Disable options without crm_device_id (default: true)
 * @param {Boolean} showSelectAll - Show "Select All" option (default: true, only works when multiple=true)
 */
const UserDetailsSelectWithSearch = ({
  value,
  onChange,
  placeholder = "Select Users",
  className = "form-control store_input_field",
  style = { width: '100%' },
  maxTagCount = 3,
  showSearch = true,
  mode = "tags",
  userTypes = ["BDM", "Manager", "Sales Supervisor", "Sales Person"],
  condition = {},
  disabled = false,
  disableOptions = true,
  multiple = true,
  showSelectAll = true,
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch user data
  const getUserData = async () => {
    try {
      setLoading(true);
      const requestOptions = {
        type: "",
        condition: {
          users_type: { $in: userTypes },
          status: "A",
          ...condition,
        },
        sort: { users_name: 1, users_type: -1 },
        select: { 
          users_name: 1, 
          last_name: 1, 
          users_email: 1, 
          users_mobile: 1, 
          users_type: 1, 
          crm_device_id: 1,
          pos_number : 1 
        },
      };
      
      const res = await getUserList(requestOptions);
      
      if (res?.status === true) {
        const userOptions = res?.result?.map((item) => ({
          label: `${item?.users_type} - ${item?.users_name?.toUpperCase()} ${item?.last_name?.toUpperCase()} (${item?.users_mobile})`,
          value: JSON.stringify(item),
          ...(disableOptions ? {disabled: item?.crm_device_id ? false : true} : {}),
          users_type: item?.users_type,
          name: `${item?.users_name?.toUpperCase()} ${item?.last_name?.toUpperCase()}`,
          mobile: item?.users_mobile,
          pos_number: item?.pos_number,
        }));

        // Add "Select All" option at the beginning (only for multiple mode)
        if (multiple && showSelectAll) {
          setOptions([
            {
              label: "SELECT ALL",
              value: "select_all",
              disabled: false,
              users_type: "All Users",
              name: "Select All Users",
              mobile: "",
            },
            ...userOptions,
          ]);
        } else {
          setOptions(userOptions);
        }
      }
      setLoading(false);
    } catch (error) {
      console.log("error : ", error);
      setLoading(false);
    }
  };

  // Load users on mount and when relevant props change
  useEffect(() => {
    getUserData();
  }, [multiple, showSelectAll]);

  // Handle selection change
  const handleChange = (selectedValues) => {
    try {
      // Single selection mode - just pass the value directly
      if (!multiple) {
        onChange && onChange(selectedValues);
        return;
      }

      // Multiple selection mode with Select All functionality
      const currentValue = value || [];
      const allUserIds = options
        .filter((option) => option.value !== "select_all" && !option.disabled)
        .map((option) => option.value);

      // Check if 'select_all' is in the new value
      const hasSelectAll = selectedValues.includes("select_all");

      // Check if all users were previously selected
      const wasAllSelected = currentValue.length === allUserIds.length && allUserIds.length > 0;

      // Remove 'select_all' from value to get actual user selections
      const filteredValue = selectedValues.filter((v) => v !== "select_all");

      // Scenario 1: User clicked 'Select All' from dropdown
      if (hasSelectAll && !wasAllSelected) {
        onChange && onChange(allUserIds);
      }
      // Scenario 2: User removed the 'Select All' tag
      else if (wasAllSelected && !hasSelectAll && filteredValue.length === 0) {
        onChange && onChange([]);
      }
      // Scenario 3: Normal operation - user is adding/removing individual users
      else {
        onChange && onChange(filteredValue);
      }
    } catch (error) {
      console.log("error : ", error);
    }
  };

  // Calculate display value
  const displayValue = (() => {
    // Single selection mode - return value as is
    if (!multiple) {
      return value || undefined;
    }

    // Multiple selection mode - handle Select All
    const currentValue = value || [];
    const allUserIds = options
      .filter((option) => option.value !== "select_all" && !option.disabled)
      .map((option) => option.value);
    
    // Show "Select All" if all users are selected
    if (showSelectAll && currentValue.length === allUserIds.length && allUserIds.length > 0) {
      return ["select_all", ...currentValue];
    }
    return currentValue;
  })();

  return (
    <Select
      className={className}
      mode={multiple ? mode : undefined}
      style={style}
      placeholder={placeholder}
      value={displayValue}
      onChange={handleChange}
      options={options}
      showSearch={showSearch}
      loading={loading}
      disabled={disabled}
      maxTagCount={multiple ? maxTagCount : undefined}
      maxTagPlaceholder={multiple ? (omittedValues) => `+${omittedValues.length}` : undefined}
      dropdownStyle={{ zIndex: 10000 }}
      getPopupContainer={(trigger) => trigger.parentNode}
      allowClear
      filterOption={(input, option) => {
        const searchValue = input.toLowerCase();
        return (
          option.users_type?.toLowerCase()?.includes(searchValue) ||
          option.name?.toLowerCase()?.includes(searchValue) ||
          option.mobile?.toString()?.toLowerCase()?.includes(searchValue) ||
          option.pos_number?.toString()?.toLowerCase()?.includes(searchValue) ||
          option.label?.toLowerCase()?.includes(searchValue)
        );
      }}
      optionRender={(option) => (
        <div>
          <div
            style={{
              fontWeight: 600,
              color: option.data.value === "select_all" ? "#1890ff" : "inherit",
            }}
          >
            {option.data.users_type}
          </div>
          <div
            style={{
              fontWeight: option.data.value === "select_all" ? 600 : "normal",
            }}
          >
            {option.data.name}
          </div>
          {option.data.mobile && (
            <div style={{ fontSize: 12, color: "#888" }}>
              {option.data.mobile} || {option.data.pos_number}
            </div>
          )}
        </div>
      )}
    />
  );
};

export default UserDetailsSelectWithSearch;

